import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryCacheNotifyEvent } from '@tanstack/react-query';
import type { BookingResponse } from '@models/landing/api/api-response.model';
import { useAppStore, selectLanguage } from '@store/app.store';
import { useSkywardsSettings } from '@shared/components/Layout/Header/useSkywardsSettings';
import { formatTime } from '@utils/date-time.util';
import {
  getCheckinCountdownState,
  type CheckinCountdownStatus,
} from '@utils/checkin-countdown.util';

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
/** Same key used by useRetrievePnr — read-only here, never fetched from this hook. */
const RETRIEVE_QUERY_KEY = ['retrieve', 'pnrMetaData'] as const;

export interface CheckinCountdownResult {
  status: CheckinCountdownStatus;
  isPopupOpen: boolean;
  remainingMinutesLabel: string;
  closingTimeLabel: string;
  onDismiss: () => void;
  onSessionExpiredConfirm: () => void;
}

const secondsUntil = (endTime: Date): number =>
  Math.max(Math.round((endTime.getTime() - Date.now()) / MILLISECONDS_PER_SECOND), 0);

const isRetrieveQueryKey = (queryKey: readonly unknown[]): boolean =>
  queryKey.length === RETRIEVE_QUERY_KEY.length &&
  queryKey.every((part, index) => part === RETRIEVE_QUERY_KEY[index]);

/** Read-only observer of the shared retrieve-PNR cache — never fetches/refetches itself. */
const useRetrievedBookingResponse = (): BookingResponse | undefined => {
  const queryClient = useQueryClient();
  const [bookingResponse, setBookingResponse] = useState(() =>
    queryClient.getQueryData<BookingResponse>(RETRIEVE_QUERY_KEY),
  );

  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event: QueryCacheNotifyEvent) => {
        if (isRetrieveQueryKey(event.query.queryKey as readonly unknown[])) {
          setBookingResponse(queryClient.getQueryData<BookingResponse>(RETRIEVE_QUERY_KEY));
        }
      }),
    [queryClient],
  );
  return bookingResponse;
};

/**
 * Drives the check-in countdown / session-expired popups from the retrieved PNR's
 * remainingTimeToCheckin + validationRules.warningTime. Mounted once at the app root.
 * Evaluated only once per session — later retrieve calls or route changes must not
 * re-trigger the warning popup. Dismissing it only hides it; a one-shot timer set up
 * at initialization still flips to "expired" exactly when the check-in window closes.
 */
export const useCheckinCountdown = (): CheckinCountdownResult => {
  const bookingResponse = useRetrievedBookingResponse();
  const { homepageUrl } = useSkywardsSettings();
  const language = useAppStore(selectLanguage);
  const [status, setStatus] = useState<CheckinCountdownStatus>('hidden');
  const [remainingMinutes, setRemainingMinutes] = useState(0);
  const [closingTimeLabel, setClosingTimeLabel] = useState('');
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasArrived, setHasArrived] = useState(false);
  const hasInitializedRef = useRef(false);

  const remainingTimeToCheckin = bookingResponse?.remainingTimeToCheckin;
  const warningTime = bookingResponse?.validationRules?.warningTime;

  // Flip once, as soon as the first remainingTimeToCheckin value arrives.
  useEffect(() => {
    if (!hasArrived && remainingTimeToCheckin) {
      setHasArrived(true);
    }
  }, [remainingTimeToCheckin, hasArrived]);

  // Keyed only on hasArrived so later retrieve calls never clear/reschedule this timer.
  useEffect(() => {
    if (hasInitializedRef.current || !hasArrived) {
      return undefined;
    }
    hasInitializedRef.current = true;

    const nextState = getCheckinCountdownState(remainingTimeToCheckin, warningTime);
    setStatus(nextState.status);

    if (nextState.status !== 'warning' || !nextState.endTime) {
      return undefined;
    }

    const totalSecondsLeft = secondsUntil(nextState.endTime);
    setRemainingMinutes(Math.ceil(totalSecondsLeft / SECONDS_PER_MINUTE));
    setClosingTimeLabel(formatTime(nextState.endTime, language));

    const timeoutId = window.setTimeout(() => {
      try {
        setStatus('expired');
      } finally {
        window.clearTimeout(timeoutId);
      }
    }, totalSecondsLeft * MILLISECONDS_PER_SECOND);

    return (): void => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- must run exactly once, on the hasArrived transition
  }, [hasArrived]);

  const onSessionExpiredConfirm = useCallback((): void => {
    if (homepageUrl) {
      window.location.href = homepageUrl;
    }
  }, [homepageUrl]);

  const onDismiss = useCallback((): void => {
    setIsDismissed(true);
  }, []);

  return {
    status,
    isPopupOpen: status === 'warning' && !isDismissed,
    remainingMinutesLabel: String(remainingMinutes),
    closingTimeLabel,
    onDismiss,
    onSessionExpiredConfirm,
  };
};
